import CRMService from "@/src/api/CRMService.js";
import TicketsService from "@/src/api/TicketsService.js";
import ProjectService from "@/src/api/ProjectService.js";
import ApprovalsService from "@/src/api/ApprovalsService.js";
import { entityTypes } from "@/src/constants/entityTypeConstants.js";
import { deduplicateRelationships } from "@/src/utils/dynamicCRMViewUtils.js";
import {
  EXCLUDED_GROUP_BY_FIELD_IDS,
  STANDARD_OBJECT_PRIMARY_ATTR_IDS,
  SYSTEM_SLUG_SERVICE,
} from "@/src/constants/crmConstants.js";

// Cache TTL for in-memory schema caching
const SCHEMA_CACHE_TTL = 15 * 60 * 1000; // 15 minutes

const state = {
  schemas: {},
  loading: {},
  pendingRequests: {}, // Store promises for concurrent requests
};

// Build the cache key for a (slug, projectId, service) triple. Two services
// can own the same slug (e.g. a CRM products alongside the products-service
// products), so service must be part of the key when present.
const schemaCacheKey = (slug, projectId = null, service = null) => {
  if (projectId !== null && projectId !== undefined) {
    return `tasks_${projectId}`;
  }
  return service ? `${slug}::${service}` : slug;
};

// Look up a schema entry with a service-aware fallback chain. Required because
// callers that don't know a slug's owning service still need to hit the
// service-keyed bucket populated by fetchSchema (which resolves service via
// SYSTEM_SLUG_SERVICE). Without this, standard-slug schemas like `deals` —
// stored under `deals::crm` — would be invisible to bare-slug getters.
const lookupSchemaEntry = (bucket, slug, projectId, service) => {
  const key = schemaCacheKey(slug, projectId, service);
  if (bucket[key]) return bucket[key];
  if (!service) {
    if (bucket[slug]) return bucket[slug];
    const pinnedService = SYSTEM_SLUG_SERVICE[String(slug).toLowerCase()];
    if (pinnedService) {
      const pinnedKey = schemaCacheKey(slug, projectId, pinnedService);
      if (bucket[pinnedKey]) return bucket[pinnedKey];
    }
  }
  return null;
};

const getters = {
  getSchemaBySlug:
    (state) =>
    (slug, projectId = null, service = null) =>
      lookupSchemaEntry(state.schemas, slug, projectId, service),
  isSchemaLoading:
    (state) =>
    (slug, projectId = null, service = null) =>
      !!lookupSchemaEntry(state.loading, slug, projectId, service),
  getSelectFieldsBySlug:
    (state) =>
    (slug, projectId = null, options = {}, service = null) => {
      const schema = lookupSchemaEntry(
        state.schemas,
        slug,
        projectId,
        service,
      );
      if (!schema || !schema.attributes) return [];

      let fields = schema.attributes.filter((attr) => attr.type === "select");

      if (options.excludeGroupBy) {
        // Filter out fields that are excluded from group by using both id and slug
        // since slug can be changed but id remains constant
        fields = fields.filter(
          (attr) =>
            !EXCLUDED_GROUP_BY_FIELD_IDS.includes(attr.id) &&
            !EXCLUDED_GROUP_BY_FIELD_IDS.includes(attr.slug),
        );
      }

      return fields;
    },
  getAllFieldsBySlug:
    (state) =>
    (slug, projectId = null, service = null) => {
      const schema = lookupSchemaEntry(
        state.schemas,
        slug,
        projectId,
        service,
      );
      if (!schema || !schema.attributes) return [];

      return schema.attributes.filter(
        (attr) =>
          attr.name &&
          attr.name.trim() !== "" &&
          attr.slug &&
          attr.slug.trim() !== "" &&
          attr.slug !== "id",
      );
    },
  getPrimaryAttributeSlugBySlug:
    (state) =>
    (slug, projectId = null, service = null) => {
      const schema = lookupSchemaEntry(
        state.schemas,
        slug,
        projectId,
        service,
      );
      console.log("[schema/getPrimaryAttributeSlugBySlug]", {
        slug,
        service,
        projectId,
        lookupKey: schemaCacheKey(slug, projectId, service),
        schemaFound: !!schema,
        attributeCount: schema?.attributes?.length ?? 0,
      });
      if (!schema || !schema.attributes) return "";

      const primaryAttr = schema.attributes.find(
        (attr) => attr?.is_primary === true,
      );
      if (primaryAttr?.slug) {
        console.log("[schema/getPrimaryAttributeSlugBySlug] primary found", {
          slug,
          service,
          primarySlug: primaryAttr.slug,
        });
        return primaryAttr.slug;
      }

      // Fallback for standard objects
      const standardPrimaryAttrIds =
        STANDARD_OBJECT_PRIMARY_ATTR_IDS[slug?.toLowerCase()];
      if (standardPrimaryAttrIds) {
        const ids = Array.isArray(standardPrimaryAttrIds) ? standardPrimaryAttrIds : [standardPrimaryAttrIds];
        for (const attrId of ids) {
          const standardAttr = schema.attributes.find(
            (attr) => attr?.id === attrId,
          );
          if (standardAttr?.slug) {
            console.log(
              "[schema/getPrimaryAttributeSlugBySlug] standard-id fallback",
              { slug, service, attrId, primarySlug: standardAttr.slug },
            );
            return standardAttr.slug;
          }
        }
      }

      console.log(
        "[schema/getPrimaryAttributeSlugBySlug] no primary found",
        { slug, service },
      );
      return "";
    },
  getPrimaryAttributeBySlug:
    (state) =>
    (slug, projectId = null, service = null) => {
      const schema = lookupSchemaEntry(
        state.schemas,
        slug,
        projectId,
        service,
      );
      if (!schema || !schema.attributes) return null;

      let primaryAttr = schema.attributes.find(
        (attr) => attr?.is_primary === true,
      );

      // Fallback for standard objects
      if (!primaryAttr) {
        const standardPrimaryAttrIds =
          STANDARD_OBJECT_PRIMARY_ATTR_IDS[slug?.toLowerCase()];
        if (standardPrimaryAttrIds) {
          const ids = Array.isArray(standardPrimaryAttrIds) ? standardPrimaryAttrIds : [standardPrimaryAttrIds];
          for (const attrId of ids) {
            primaryAttr = schema.attributes.find(
              (attr) => attr?.id === attrId,
            );
            if (primaryAttr) break;
          }
        }
      }

      if (!primaryAttr) return null;
      return {
        slug: primaryAttr.slug || "",
        name: primaryAttr.name || "",
      };
    },
};

const mutations = {
  SET_SCHEMA(state, { key, schema }) {
    state.schemas = {
      ...state.schemas,
      [key]: {
        ...schema,
        timestamp: Date.now(),
      },
    };
  },
  SET_LOADING(state, { key, loading }) {
    state.loading = {
      ...state.loading,
      [key]: loading,
    };
  },
  CLEAR_SCHEMA(state, key) {
    const { [key]: removed, ...rest } = state.schemas;
    state.schemas = rest;
  },
  CLEAR_ALL_SCHEMAS(state) {
    state.schemas = {};
    state.loading = {};
    state.pendingRequests = {};
  },
  SET_PENDING_REQUEST(state, { key, promise }) {
    state.pendingRequests[key] = promise;
  },
  CLEAR_PENDING_REQUEST(state, key) {
    delete state.pendingRequests[key];
  },
};

const actions = {
  async fetchSchema(
    { commit, state, rootGetters },
    { slug, projectId = null, force = false, service = null },
  ) {
    // Resolve service up-front so it's part of the cache key. Two services
    // can own the same slug; without service in the key they'd collide.
    // Fallback order: caller-provided → SYSTEM_SLUG_SERVICE hard-pin (synchronous,
    // survives hard refresh before objects store hydrates) → objects store.
    const resolvedService =
      service ||
      SYSTEM_SLUG_SERVICE[String(slug).toLowerCase()] ||
      rootGetters["objects/getObjectBySlugOrId"]?.(slug)?.service ||
      null;
    const key = schemaCacheKey(slug, projectId, resolvedService);

    // 1. Check in-memory Vuex state cache first
    const existingSchema = state.schemas[key];
    if (!force && existingSchema) {
      const age = Date.now() - existingSchema.timestamp;
      
      if (age < SCHEMA_CACHE_TTL) {
        return { success: true, schema: existingSchema };
      }
    }

    // 2. If already fetching, return the SAME promise (promise sharing)
    // But respect force flag - if force=true, ignore pending request and fetch fresh
    if (!force && state.pendingRequests[key]) {
      return state.pendingRequests[key];
    }

    // 3. Create and store promise for concurrent requests to share
    const promise = (async () => {
      try {
        commit("SET_LOADING", { key, loading: true });

        let response;
      if (slug === entityTypes.TICKETS) {
        response = await TicketsService.getTicketSchema();
      } else if (slug === entityTypes.APPROVALS) {
        response = await ApprovalsService.getApprovalSchema();
      } else if (
        slug === entityTypes.TASKS &&
        projectId !== null &&
        projectId !== undefined
      ) {
        response = await ProjectService.tasks.fetchTaskSchema(projectId);
      } else {
        response = await CRMService.fetchSchemaObject(slug, resolvedService);
      }

      if (response?.success) {
        let schemaToStore = response.schema;

        if (schemaToStore?.relationships) {
          const deduplicatedRelationships = deduplicateRelationships(
            schemaToStore.relationships,
          );

          // Create a map of valid relationship IDs
          const validRelationshipIds = new Set(
            deduplicatedRelationships.map((rel) => rel.id),
          );

          // Filter attributes to only include those with valid relationship IDs
          let filteredAttributes = schemaToStore.attributes || [];
          if (schemaToStore?.attributes) {
            const beforeCount = filteredAttributes.length;
            filteredAttributes = filteredAttributes.filter((attr) => {
              if (String(attr.type || "").toLowerCase() === "relationship") {
                const isValid = validRelationshipIds.has(attr.id);

                return isValid;
              }
              return true;
            });
          }

          schemaToStore = {
            ...schemaToStore,
            relationships: deduplicatedRelationships,
            attributes: filteredAttributes,
          };
        }


        if (
          slug === entityTypes.TASKS &&
          projectId !== null &&
          projectId !== undefined &&
          schemaToStore?.attributes
        ) {
          schemaToStore = {
            ...schemaToStore,
            attributes: schemaToStore.attributes.filter(
              (attr) => attr.slug !== "project_id",
            ),
          };
        }

        commit("SET_SCHEMA", {
          key,
          schema: schemaToStore,
        });
        
        return { success: true, schema: schemaToStore };
      } else {
        console.error(`Failed to fetch schema for ${key}:`, response?.errors);
        return { success: false, error: response?.errors };
      }
      } catch (error) {
        console.error(`Error fetching schema for ${key}:`, error);
        return { success: false, error: error.message };
      } finally {
        commit("SET_LOADING", { key, loading: false });
        commit("CLEAR_PENDING_REQUEST", key);
      }
    })();

    commit("SET_PENDING_REQUEST", { key, promise });
    return promise;
  },

  async refreshSchema({ dispatch }, { slug, projectId = null, service = null }) {
    // Forward service — without it fetchSchema falls back to SYSTEM_SLUG_SERVICE
    // and hits the wrong service's catalog for shared-slug objects
    // (e.g. refresh for a CRM-scoped `products` would hit service=products).
    return dispatch("fetchSchema", { slug, projectId, service, force: true });
  },

  clearSchema({ commit }, { slug, projectId = null, service = null }) {
    const key = schemaCacheKey(slug, projectId, service);
    commit("CLEAR_SCHEMA", key);
  },

  clearAllSchemas({ commit }) {
    commit("CLEAR_ALL_SCHEMAS");
  },
};

export default {
  namespaced: true,
  state,
  getters,
  mutations,
  actions,
};
